import { PlayerEvaluationDataResponse, PlayerEvaluationSchema, ProgramEvaluationSchema } from '../types/Evaluation';

function formatNowAsSqlDateTime(): string {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Creates the form field name key for a given tab and field index.
 * Using a flat key avoids deeply nested form state and improves React Hook Form performance.
 */
export function getFieldKey(tabIndex: number, fieldIndex: number): string {
  return `tab_${tabIndex}_field_${fieldIndex}`;
}

/**
 * Builds default form values from the API schema response.
 * Maps each field to a flat key (tab_{tabIdx}_field_{fieldIdx}) for React Hook Form.
 */
export function buildDefaultValues(schema: PlayerEvaluationSchema): Record<string, any> {
  const defaults: Record<string, any> = {};

  schema.customTabs.forEach((tab, tabIdx) => {
    tab.fields.forEach((field, fieldIdx) => {
      const key = getFieldKey(tabIdx, fieldIdx);
      defaults[key] = field.value;
    });
  });

  return defaults;
}

/**
 * Transforms flat form data back into the API schema structure for submission.
 * Reconstructs the customTabs array with updated field values.
 */
export function transformFormDataForApi(
  formData: Record<string, any>,
  schema: PlayerEvaluationSchema,
): PlayerEvaluationSchema {
  return {
    ...schema,
    customTabs: schema.customTabs.map((tab, tabIdx) => ({
      ...tab,
      fields: tab.fields.map((field, fieldIdx) => {
        const key = getFieldKey(tabIdx, fieldIdx);
        return {
          ...field,
          value: formData[key] !== undefined ? formData[key] : field.value,
        };
      }),
    })),
  };
}

function mapDisplayOptionToStoredValue(
  value: any,
  possibleValues: Record<string, any> | undefined,
): any {
  if (!possibleValues || value === undefined || value === null) return value;

  const toStored = (item: any) => {
    const itemAsString = String(item);
    const directMatch = Object.prototype.hasOwnProperty.call(possibleValues, itemAsString);
    if (directMatch) return item;

    const matchedKey = Object.keys(possibleValues).find(
      key => String(possibleValues[key]) === itemAsString,
    );
    return matchedKey !== undefined ? matchedKey : item;
  };

  if (Array.isArray(value)) {
    return value.map(toStored);
  }

  return toStored(value);
}

export function transformFormDataToPlayerEvaluationData(
  formData: Record<string, any>,
  schema: PlayerEvaluationSchema,
  playerEvaluationData: PlayerEvaluationDataResponse | null | undefined,
): PlayerEvaluationDataResponse {
  const existingCustomFields = playerEvaluationData?.customFields || {};
  const nextCustomFields: Record<string, any> = { ...existingCustomFields };

  schema.customTabs.forEach((tab, tabIdx) => {
    tab.fields.forEach((field, fieldIdx) => {
      const fieldName = field.fieldName;
      if (!fieldName) return;

      const key = getFieldKey(tabIdx, fieldIdx);
      const rawValue = formData[key] !== undefined ? formData[key] : field.value;
      const normalizedValue = mapDisplayOptionToStoredValue(rawValue, field.possibleValues);
      const existingField = existingCustomFields[fieldName];
      const normalizedFieldType = normalizeFieldType(String(field.fieldType || 'TEXT'));

      if (normalizedFieldType === 'TEXT_MULTI' && Array.isArray(normalizedValue)) {
        const existingNotes = Array.isArray(existingField) ? existingField : [];

        nextCustomFields[fieldName] = normalizedValue
          .map((note, idx) => {
            const value = String(note ?? '').trim();
            if (!value) return null;

            return {
              value,
              index: String(idx),
              created: existingNotes[idx]?.created || formatNowAsSqlDateTime(),
            };
          })
          .filter(Boolean);

        return;
      }

      if (existingField && typeof existingField === 'object' && !Array.isArray(existingField)) {
        nextCustomFields[fieldName] = {
          ...existingField,
          value: normalizedValue,
        };
      } else {
        nextCustomFields[fieldName] = {
          value: normalizedValue,
        };
      }
    });
  });

  return {
    accountName: playerEvaluationData?.accountName || '',
    playerCode: playerEvaluationData?.playerCode || String(schema.playerId || ''),
    customFields: nextCustomFields,
  };
}

function toNumber(value: any, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeFieldType(fieldType: string) {
  if (fieldType === 'NUMBER') return 'NUMERIC';
  return fieldType;
}

function toOptions(possibleValues: Record<string, any> | undefined): string[] | undefined {
  if (!possibleValues) return undefined;
  return Object.values(possibleValues).map(String);
}

function toFieldValue(fieldType: string, value: any): any {
  if (value === undefined || value === null) {
    if (fieldType === 'MULTISELECT' || fieldType === 'TEXT_MULTI') return [];
    if (fieldType === 'NUMERIC' || fieldType === 'NUMBER') return '';
    return '';
  }

  if (fieldType === 'MULTISELECT') {
    return Array.isArray(value) ? value : [String(value)];
  }

  if (fieldType === 'TEXT_MULTI') {
    if (Array.isArray(value)) return value.map(String);
    return [String(value)];
  }

  return value;
}

function extractFieldValue(rawField: any): any {
  if (Array.isArray(rawField)) {
    return rawField.map(item => {
      if (item && typeof item === 'object' && 'value' in item) {
        return item.value;
      }
      return item;
    });
  }

  if (rawField && typeof rawField === 'object' && 'value' in rawField) {
    return rawField.value;
  }

  return rawField;
}

function mapStoredOptionToDisplayValue(
  value: any,
  possibleValues: Record<string, any> | undefined,
): any {
  if (!possibleValues || value === undefined || value === null) return value;

  if (Array.isArray(value)) {
    return value.map(item => {
      const key = String(item);
      return possibleValues[key] !== undefined ? possibleValues[key] : item;
    });
  }

  const key = String(value);
  return possibleValues[key] !== undefined ? possibleValues[key] : value;
}

// Transform program schema + player evaluation data to UI form values.
export function transformApiResponseToFormValues(
  programSchema: ProgramEvaluationSchema | null,
  playerEvaluationData: PlayerEvaluationDataResponse | null,
): PlayerEvaluationSchema | undefined {
  if (!programSchema) return undefined;
  const customFieldValues = playerEvaluationData?.customFields || {};
  const getCustomFieldValue = (fieldName: string): any => extractFieldValue(customFieldValues[fieldName]);
  const getPossibleValuesForField = (fieldName: string): Record<string, any> | undefined => {
    const matchedField = (programSchema.customFields || []).find(field => field.fieldName === fieldName);
    return matchedField?.possibleValues || undefined;
  };
  const defaultScorecardFields = new Set(['evalScore', 'evalCode', 'evalColor']);

  const sortedTabs = [...(programSchema.customTabs || [])].sort(
    (a, b) => toNumber(a.tabOrder, 0) - toNumber(b.tabOrder, 0),
  );

  const customTabs = sortedTabs.map(tab => {
    const currentTabId = String(tab.id);
    const currentTabName = (tab.tabName || '').toLowerCase();

    const tabFields = (programSchema.customFields || [])
      .filter(field => {
        const fieldTabId = field.programCustomTabId !== undefined && field.programCustomTabId !== null
          ? String(field.programCustomTabId)
          : undefined;
        const hasTabIdMatch = fieldTabId ? fieldTabId === currentTabId : false;
        const hasTabNameMatch = (field.fieldTab || '').toLowerCase() === currentTabName;
        return hasTabIdMatch || hasTabNameMatch;
      })
      .filter(field => !(currentTabName === 'default' && defaultScorecardFields.has(field.fieldName)))
      .sort((a, b) => toNumber(a.fieldOrder, 0) - toNumber(b.fieldOrder, 0))
      .map(field => {
        const fieldType = normalizeFieldType(String(field.fieldType || 'TEXT')) as any;
        const fieldDisplayName = field.fieldDisplayName ?? undefined;
        const fieldHelpText = field.fieldHelpText ?? undefined;
        const normalizedFieldValue = mapStoredOptionToDisplayValue(
          getCustomFieldValue(field.fieldName || ''),
          field.possibleValues || undefined,
        );

        return {
          name: fieldDisplayName || field.fieldName || '',
          type: fieldType,
          value: toFieldValue(fieldType, normalizedFieldValue),
          selectionMessage: fieldHelpText,
          options: toOptions(field.possibleValues || undefined),
          fieldOrder: String(field.fieldOrder),
          fieldName: field.fieldName,
          fieldDisplayName,
          fieldType,
          fieldDefaultValue: field.fieldDefaultValue,
          fieldHelpText,
          id: String(field.id),
          readOnly: field.readOnly || field.readonly || undefined,
          possibleValues: field.possibleValues || undefined,
        };
      });

    return {
      id: String(tab.id),
      tabOrder: String(tab.tabOrder),
      tabName: tab.tabName,
      name: tab.tabName || '',
      fields: tabFields,
    };
  });

  return {
    playerId: toNumber(playerEvaluationData?.playerCode, 0),
    playerEvaluationScore: toNumber(getCustomFieldValue('evalScore'), 0),
    playerEvaluationCode: String(getCustomFieldValue('evalCode') || ''),
    evaluationColor: String(getCustomFieldValue('evalColor') || '#333333'),
    playerDisplay: String(getCustomFieldValue('playerDisplay') || 'all'),
    userNotes: String(getCustomFieldValue('userNotes') || ''),
    accountNotes: Array.isArray(customFieldValues.accountNotes)
      ? customFieldValues.accountNotes.map((note: any, idx: number) => ({
          note: String(note?.value ?? note?.note ?? note ?? ''),
          createdBy: Number(note?.createdBy || 0),
          createdDate: String(note?.created || note?.createdDate || `1970-01-${String(idx + 1).padStart(2, '0')}`),
        }))
      : [],
    nilContributionsContributed: toNumber(getCustomFieldValue('nilContributed'), 0),
    nilContributionsCommitted: toNumber(getCustomFieldValue('nilCommitted'), 0),
    scholarshipStatus: String(
      mapStoredOptionToDisplayValue(
        getCustomFieldValue('scholarshipStatus'),
        getPossibleValuesForField('scholarshipStatus'),
      ) || '',
    ),
    eligibilityRemaining: String(getCustomFieldValue('eligibilityRemaining') || ''),
    boardCardPrimaryColor: String(getCustomFieldValue('primaryBoardColor') || '#000000'),
    boardCardSecondaryColor: String(getCustomFieldValue('secondaryBoardColor') || '#000000'),
    customTabs,
  };
}